import type { ScreeningCategoryCode, ScreeningModalityCode } from '@liza/fhir-utils';
import type { PlanDefinitionAction } from '@medplum/fhirtypes';
import { add, isAfter, parseISO } from 'date-fns';
import { ageYears, latestEvidence, patientOf, resourceInstant, sexOf, type ContextResources } from '../planner/context';
import type { ProtocolSpec } from '../planner/plan';
import type { ProtocolVariables } from '../planner/types';
import { PREVENTION_SCREENING_ACTIONS, preventionScreening } from './preventionScreening';

/** When a screening action's coverage runs out: the last study of its modality plus its interval. */
const coveredUntil = (action: PlanDefinitionAction, resources: ContextResources): Date | undefined => {
  const instant = resourceInstant(latestEvidence(resources, action));
  const repeat = action.timingTiming?.repeat;
  if (!instant || !repeat?.period || repeat.periodUnit !== 'a') return undefined;
  return add(parseISO(instant), { years: repeat.period });
};

/**
 * The modality whose most recent study still covers the category, as of `now` — the one covering
 * longest when several do — or undefined when none does. This is the category logic the guidelines
 * leave implicit: a colonoscopy ten years ago covers colorectal screening, so FIT is not also due.
 */
export const coveringModality = (
  category: ScreeningCategoryCode,
  resources: ContextResources,
  now: Date,
): ScreeningModalityCode | undefined => {
  const covering = PREVENTION_SCREENING_ACTIONS[category]
    .map((action) => ({ modality: action.id as ScreeningModalityCode, until: coveredUntil(action, resources) }))
    .filter(
      (c): c is { modality: ScreeningModalityCode; until: Date } => c.until !== undefined && isAfter(c.until, now),
    )
    .sort((a, b) => b.until.getTime() - a.until.getTime());
  return covering[0]?.modality;
};

/** Binds exactly the variables `PREVENTION_CONTEXT_VARIABLES` declares (a test pins the two lists to each other). */
export const derivePreventionVariables = (resources: ContextResources, now: Date): ProtocolVariables => {
  const patient = patientOf(resources);
  return {
    '%ageYears': { kind: 'integer', value: ageYears(patient, now) },
    '%sex': { kind: 'string', value: sexOf(patient, now)?.sex },
    '%colorectalCoveredBy': { kind: 'string', value: coveringModality('colorectal-cancer', resources, now) },
    '%cervicalCoveredBy': { kind: 'string', value: coveringModality('cervical-cancer', resources, now) },
  };
};

/**
 * The prevention protocol, ready for `planProtocol`. No extra queries: the patient is read by every
 * apply, and each action's evidence queries are generated from its `input` requirements.
 */
export const preventionScreeningProtocol: ProtocolSpec = {
  planDefinition: preventionScreening,
  valueSets: [],
  queries: [],
  deriveVariables: derivePreventionVariables,
};
